import { getFunctions, httpsCallable } from 'firebase/functions'
import { getFirestore, collection, query, orderBy, limit, getDocs, where, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore'

const functions = getFunctions()
const db = getFirestore()

export interface Email {
  id: string
  messageId: string
  from: string
  fromName: string
  to: string
  subject: string
  date: Date
  snippet: string
  body: string
  htmlBody: string | null
  isRead: boolean
  hasAttachments: boolean
  folder: string
  accountId: string
  accountEmail: string
  accountColor: string
  // Vendor relationship
  vendorId?: string
  vendorName?: string
  vendorDomain?: string
  // User action tracking (for Cleared tab)
  userAction?: 'not_interested' | 'cleared' | null
  userActionAt?: Date
  deleteOnExpiry?: boolean
}

export interface FetchResult {
  success: boolean
  accounts?: number
  totalEmails?: number
  results?: Array<{ email: string; count?: number; error?: string }>
  error?: string
}

export interface SyncResult {
  success: boolean
  status: 'complete' | 'in_progress' | 'no_accounts'
  newInChunk?: number
  totalSynced: number
  oldestDate?: string | null
  oldestDateISO?: string | null
  isComplete: boolean
  reachedCutoff?: boolean
  message: string
}

export interface SyncStatus {
  totalSynced: number
  oldestDate: string | null
  isComplete: boolean
  retentionDays: number
  lastSyncAt: string | null
}

/**
 * Sync emails in chunks with progress tracking
 */
export async function syncEmailsChunked(chunkSize: number = 200, folder: string = 'INBOX'): Promise<SyncResult> {
  const syncFn = httpsCallable(functions, 'syncEmailsChunked')

  try {
    const result = await syncFn({ chunkSize, folder })
    return result.data as SyncResult
  } catch (error: unknown) {
    const err = error as { message?: string }
    console.error('Failed to sync emails:', err)
    return {
      success: false,
      status: 'in_progress',
      totalSynced: 0,
      isComplete: false,
      message: err.message || 'Failed to sync emails'
    }
  }
}

/**
 * Get current sync status
 */
export async function getSyncStatus(): Promise<SyncStatus | null> {
  const statusFn = httpsCallable(functions, 'getSyncStatus')

  try {
    const result = await statusFn({})
    return result.data as SyncStatus
  } catch (error: unknown) {
    console.error('Failed to get sync status:', error)
    return null
  }
}

export type UserAction = 'not_interested' | 'cleared'

export interface MarkEmailsResult {
  success: boolean
  markedCount: number
  action: UserAction
}

export interface ClearedEmailsResult {
  success: boolean
  emails: Email[]
  count: number
}

export interface DeleteResult {
  success: boolean
  deletedCount: number
}

/**
 * Mark an email as read in Firestore
 * The read status will be synced to IMAP on the next scheduled fetch
 */
export async function markEmailAsRead(userId: string, emailId: string): Promise<void> {
  const emailRef = doc(db, 'users', userId, 'emails', emailId)
  await updateDoc(emailRef, {
    isRead: true,
    readAt: serverTimestamp(),
    needsImapSync: true  // Flag to sync to IMAP
  })
}

/**
 * Mark emails with user action (not_interested or cleared)
 */
export async function markEmails(options: {
  emailIds?: string[]
  vendorId?: string
  action: UserAction
  deleteOnExpiry?: boolean
}): Promise<MarkEmailsResult> {
  const markFn = httpsCallable(functions, 'markEmails')

  try {
    const result = await markFn(options)
    return result.data as MarkEmailsResult
  } catch (error: unknown) {
    console.error('Failed to mark emails:', error)
    return { success: false, markedCount: 0, action: options.action }
  }
}

/**
 * Get cleared/dismissed emails
 */
export async function getClearedEmails(limit: number = 100): Promise<ClearedEmailsResult> {
  const getFn = httpsCallable(functions, 'getClearedEmails')

  try {
    const result = await getFn({ limit })
    return result.data as ClearedEmailsResult
  } catch (error: unknown) {
    console.error('Failed to get cleared emails:', error)
    return { success: false, emails: [], count: 0 }
  }
}

/**
 * Delete cleared emails
 */
export async function deleteClearedEmails(options?: {
  emailIds?: string[]
  deleteFromServer?: boolean
}): Promise<DeleteResult> {
  const deleteFn = httpsCallable(functions, 'deleteClearedEmails')

  try {
    const result = await deleteFn(options || {})
    return result.data as DeleteResult
  } catch (error: unknown) {
    console.error('Failed to delete cleared emails:', error)
    return { success: false, deletedCount: 0 }
  }
}

/**
 * Fetch emails from all connected accounts
 */
export async function fetchAllEmailsFromAccounts(folder: string = 'INBOX', emailLimit: number = 50): Promise<FetchResult> {
  const fetchAllEmails = httpsCallable(functions, 'fetchAllEmails')

  try {
    const result = await fetchAllEmails({ folder, limit: emailLimit })
    return result.data as FetchResult
  } catch (error: unknown) {
    const err = error as { message?: string }
    console.error('Failed to fetch emails:', err)
    return { success: false, error: err.message || 'Failed to fetch emails' }
  }
}

/**
 * Fetch emails for a specific account
 */
export async function fetchEmailsFromAccount(accountId: string, folder: string = 'INBOX', emailLimit: number = 50): Promise<FetchResult> {
  const fetchEmails = httpsCallable(functions, 'fetchEmails')

  try {
    const result = await fetchEmails({ accountId, folder, limit: emailLimit })
    return result.data as FetchResult
  } catch (error: unknown) {
    const err = error as { message?: string }
    console.error('Failed to fetch emails:', err)
    return { success: false, error: err.message || 'Failed to fetch emails' }
  }
}

/**
 * Test connection for an account
 */
export async function testAccountConnection(accountId: string): Promise<{ success: boolean; error?: string }> {
  const testConnection = httpsCallable(functions, 'testEmailConnection')

  try {
    const result = await testConnection({ accountId })
    return result.data as { success: boolean }
  } catch (error: unknown) {
    const err = error as { message?: string }
    console.error('Connection test failed:', err)
    return { success: false, error: err.message || 'Connection test failed' }
  }
}

/**
 * Get emails from local Firestore cache
 */
export async function getEmailsFromCache(
  userId: string,
  folder: string = 'INBOX',
  emailLimit: number = 50
): Promise<Email[]> {
  try {
    const emailsRef = collection(db, 'users', userId, 'emails')
    const q = query(
      emailsRef,
      where('folder', '==', folder),
      orderBy('date', 'desc'),
      limit(emailLimit)
    )

    const snapshot = await getDocs(q)

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date()
    })) as Email[]
  } catch (error) {
    console.error('Failed to get emails from cache:', error)
    return []
  }
}

/**
 * Get all emails from cache (both inbox and sent)
 */
export async function getAllEmailsFromCache(userId: string, emailLimit: number = 100): Promise<Email[]> {
  try {
    const emailsRef = collection(db, 'users', userId, 'emails')
    const q = query(
      emailsRef,
      orderBy('date', 'desc'),
      limit(emailLimit)
    )

    const snapshot = await getDocs(q)

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date()
    })) as Email[]
  } catch (error) {
    console.error('Failed to get emails from cache:', error)
    return []
  }
}

/**
 * Get a single email by ID
 */
export async function getEmailById(userId: string, emailId: string): Promise<Email | null> {
  try {
    const emailRef = doc(db, 'users', userId, 'emails', emailId)
    const snapshot = await getDoc(emailRef)

    if (!snapshot.exists()) {
      console.error('Email not found:', emailId)
      return null
    }

    const data = snapshot.data()
    return {
      id: snapshot.id,
      ...data,
      date: data.date?.toDate() || new Date()
    } as Email
  } catch (error) {
    console.error('Failed to get email:', error)
    return null
  }
}
